import cx from 'classnames'
import { MutableRefObject, ReactNode, useMemo, useRef, useState } from 'react'
import { useResizeObserver } from 'usehooks-ts'

import { SanityMarquee } from '@data/sanity/queries/types/modules'

import Photo from '@components/photo'
import ComplexPortableText from '@components/portable-text/complex'

type MarqueeProps = SanityMarquee

interface MarqyProps {
  speed?: number
  direction?: 'left' | 'right'
  pauseOnHover?: boolean
  children: ReactNode
}

const Marqy = ({
  speed = 0.5,
  direction = 'left',
  pauseOnHover,
  children,
}: MarqyProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const containerSize = useResizeObserver({
    ref: containerRef as MutableRefObject<HTMLDivElement>,
  })

  const [firstCopyWidth, setFirstCopyWidth] = useState<number>()

  const copyCount = useMemo(() => {
    if (!containerSize.width || !firstCopyWidth) {
      return 1
    }

    return Math.ceil(containerSize.width / firstCopyWidth)
  }, [containerSize.width, firstCopyWidth])

  const animationDuration = useMemo(() => {
    return ((firstCopyWidth ?? 0) * copyCount) / (100 * speed)
  }, [copyCount, firstCopyWidth, speed])

  const clones = new Array(2).fill(0)
  const copies = new Array(copyCount).fill(0)

  return (
    <div
      ref={containerRef}
      data-marqy=""
      data-direction={direction}
      data-pause-on-hover={pauseOnHover ? '' : null}
    >
      <div data-marqy-inner="" className="space-x-16">
        {clones.map((_, cloneIndex) => (
          <div
            key={cloneIndex}
            data-marqy-content=""
            className="space-x-16"
            style={{
              animationDuration: `${animationDuration}s`,
            }}
          >
            {copies.map((_, copyIndex) => {
              const itemCallbackRef = (element: HTMLDivElement | null) => {
                if (!element) {
                  return
                }

                // Set first copy width
                if (cloneIndex === 0 && copyIndex === 0) {
                  setFirstCopyWidth(element.getBoundingClientRect().width)
                }
              }

              return (
                <div
                  key={copyIndex}
                  ref={itemCallbackRef}
                  aria-hidden={cloneIndex > 0 || copyIndex > 0 || undefined}
                  data-marqy-item=""
                >
                  {children}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

const Marquee = ({
  content,
  contentAlignment,
  items,
  speed,
  reverse,
  pausable,
}: MarqueeProps) => {
  if (items.length === 0) {
    return null
  }

  return (
    <section className="py-40">
      {content && (
        <div className="container">
          <div
            className={cx('rc rc-marquee flex flex-col', {
              'items-start text-left': contentAlignment === 'left',
              'items-center text-center': contentAlignment === 'center',
              'items-end text-right': contentAlignment === 'right',
            })}
          >
            <ComplexPortableText content={content} />
          </div>
        </div>
      )}
      <Marqy
        speed={speed}
        direction={reverse ? 'right' : 'left'}
        pauseOnHover={pausable}
      >
        <div className="flex gap-x-16">
          {items.map((item) => {
            switch (item._type) {
              case 'simple':
                return (
                  <span key={item._key} className="block text-5xl leading-none">
                    {item.text}
                  </span>
                )

              case 'photo':
                return (
                  <div key={item._key} className="">
                    <Photo image={item.photo} />
                  </div>
                )
            }
          })}
        </div>
      </Marqy>
    </section>
  )
}

export default Marquee
